/** Money, times and distances the way the firm talks about them: pounds, UK time, miles. */

export const UK_TIME_ZONE = "Europe/London";

export function money(pence: number): string {
  const pounds = pence / 100;
  return `£${pounds.toLocaleString("en-GB", {
    minimumFractionDigits: pence % 100 === 0 && Math.abs(pence) >= 100_00 ? 0 : 2,
    maximumFractionDigits: 2,
  })}`;
}

const parts = (date: Date, options: Intl.DateTimeFormatOptions) =>
  new Intl.DateTimeFormat("en-GB", { timeZone: UK_TIME_ZONE, ...options }).format(date);

/** "14:05" */
export const time = (date: Date) =>
  parts(date, { hour: "2-digit", minute: "2-digit", hourCycle: "h23" });

/** "2026-10-09" in UK time. */
export function ukDate(date: Date): string {
  const p = new Intl.DateTimeFormat("en-CA", {
    timeZone: UK_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
  return p;
}

/** "Today", "Tomorrow" or "Sat 11 Oct". */
export function dayLabel(date: Date, now = new Date()): string {
  const day = ukDate(date);
  if (day === ukDate(now)) return "Today";
  if (day === ukDate(new Date(now.getTime() + 86_400_000))) return "Tomorrow";
  return parts(date, { weekday: "short", day: "numeric", month: "short" });
}

/** "Sun", "11", "Oct": for a calendar tile, in UK time. */
export const dateParts = (date: Date) => ({
  weekday: parts(date, { weekday: "short" }),
  day: parts(date, { day: "numeric" }),
  month: parts(date, { month: "short" }),
});

/** "Today, 14:05" */
export const when = (date: Date, now = new Date()) => `${dayLabel(date, now)}, ${time(date)}`;

/** "Thursday 9 October 2026, 14:05" */
export const longWhen = (date: Date) =>
  `${parts(date, { weekday: "long", day: "numeric", month: "long", year: "numeric" })}, ${time(date)}`;

/** "4 min", "1 hr 5 min" */
export function minutes(seconds: number): string {
  const total = Math.max(1, Math.round(seconds / 60));
  if (total < 60) return `${total} min`;
  const hours = Math.floor(total / 60);
  const rest = total % 60;
  return rest ? `${hours} hr ${rest} min` : `${hours} hr`;
}

export function miles(metres: number): string {
  const value = metres / 1609.344;
  return `${value < 10 ? value.toFixed(1) : Math.round(value)} mi`;
}

/** UK numbers as people write them: "07700 900123", "01243 000000". */
export function phone(e164: string): string {
  if (!e164.startsWith("+44")) return e164;
  const national = `0${e164.slice(3)}`;
  if (national.startsWith("07")) return `${national.slice(0, 5)} ${national.slice(5)}`;
  if (/^0(1\d1|11\d)/.test(national))
    return `${national.slice(0, 4)} ${national.slice(4, 7)} ${national.slice(7)}`;
  if (national.startsWith("02"))
    return `${national.slice(0, 3)} ${national.slice(3, 7)} ${national.slice(7)}`;
  return `${national.slice(0, 5)} ${national.slice(5)}`;
}

/** Minutes between UTC and UK time at an instant (0 in winter, 60 in summer). */
function ukOffsetMinutes(at: Date): number {
  const p = new Intl.DateTimeFormat("en-GB", {
    timeZone: UK_TIME_ZONE,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).formatToParts(at);
  const get = (type: string) => Number(p.find((x) => x.type === type)?.value);
  const asUtc = Date.UTC(get("year"), get("month") - 1, get("day"), get("hour"), get("minute"));
  return Math.round((asUtc - Math.floor(at.getTime() / 60_000) * 60_000) / 60_000);
}

/** A UK date ("2026-10-09") and time ("14:05") as an instant, wherever the phone is. */
export function fromUkLocal(date: string, clock: string): Date {
  const [y, m, d] = date.split("-").map(Number) as [number, number, number];
  const [hh, mm] = clock.split(":").map(Number) as [number, number];
  const wall = Date.UTC(y, m - 1, d, hh, mm);
  let utc = wall;
  // Twice, for times near the clocks changing.
  for (let i = 0; i < 2; i++) utc = wall - ukOffsetMinutes(new Date(utc)) * 60_000;
  return new Date(utc);
}

export const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]!.toUpperCase())
    .join("");
